import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Link as MuiLink,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { ROUTES, APP_NAME } from '../config/constants';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { SEO } from '../components/SEO';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, isLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await signIn(email, password);
    if (result.success) {
      navigate(ROUTES.DASHBOARD);
    }
  };

  return (
    <>
      <SEO 
        title="Scribelate"
        description="Sign in to your Scribelate account to continue learning languages through podcasts."
        keywords="scribelate login, sign in, language learning app"
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom textAlign="center" fontWeight="bold">
            {APP_NAME}
          </Typography>
          <Typography variant="h6" gutterBottom textAlign="center" color="text.secondary" mb={4}>
            {t('signInToAccount')}
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
            />

            <TextField
              fullWidth
              label={t('password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box textAlign="right" mt={1}>
              <MuiLink component={Link} to={ROUTES.FORGOT_PASSWORD} variant="body2" underline="hover">
                {t('forgotPassword')}
              </MuiLink>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? t('signingIn') : t('login')}
            </Button>

            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                {t('dontHaveAccount')}{' '}
                <MuiLink component={Link} to={ROUTES.REGISTER} underline="hover">
                  {t('signUp')}
                </MuiLink>
              </Typography>
            </Box>

            <Box textAlign="center" mt={3}>
              <MuiLink component={Link} to={ROUTES.HOME} variant="body2" underline="hover">
                {t('backToHome')}
              </MuiLink>
            </Box>

            <Box textAlign="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                <MuiLink component={Link} to={ROUTES.HELP} underline="hover" sx={{ mr: 2 }}>
                  {t('footer.help')}
                </MuiLink>
                <MuiLink component={Link} to={ROUTES.PRIVACY} underline="hover" sx={{ mr: 2 }}>
                  {t('footer.privacy')}
                </MuiLink>
                <MuiLink component={Link} to={ROUTES.TERMS} underline="hover" sx={{ mr: 2 }}>
                  {t('footer.terms')}
                </MuiLink>
                <MuiLink component={Link} to={ROUTES.DMCA} underline="hover">
                  {t('footer.dmca')}
                </MuiLink>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
    </>
  );
};
